import {
  useEffect,
  useRef,
  useState,
  // useCallback,
  createContext,
  useContext,
  ReactNode,
} from "react";
import { WebSocketClient } from "@/lib/client-ws";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/providers/language-provider";
import { MessageQueue, type MessageHandler } from "@/lib/message-queue";
import { SendQueue } from "@/lib/send-queue";
import { maintenanceMessage } from "@/pages/main-page";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircleIcon } from "lucide-react";

const SocketProviderContext = createContext<any>(undefined);

export function SocketProvider({
  children,
  ...props
}: {
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const wsClientRef = useRef<WebSocketClient | null>(null);
  const sendQueueRef = useRef<SendQueue>(new SendQueue());
  const messageQueueRef = useRef<MessageQueue>(new MessageQueue());

  const [isConnected, setIsConnected] = useState(false);
  // const [isReconnectable, setIsReconnectable] = useState(true);

  useEffect(() => {
    wsClientRef.current = new WebSocketClient({
      handlers: {
        onOpen: () => {
          console.log("[WS] 소켓 서버와 연결되었습니다.");
        },
        onClose: () => {
          console.log("[WS] 소켓 서버와 연결이 끊겼습니다.");
        },
        onMessage: (message) => {
          // 메시지 처리 로직
          console.log("[WS] onMessage:", message);
          messageQueueRef.current.enqueue(message);
        },
        onStateChange: (isConnected, isReconnectable) => {
          console.log(
            "[WS] Socket state changed:",
            isConnected,
            isReconnectable,
            wsClientRef.current,
          );
          setIsConnected(isConnected);
          // setIsReconnectable(isReconnectable);
          if (isConnected) {
            sendQueueRef.current.onConnected();
          }
        },
      },
    });

    // SendQueue에 sendFn 설정
    sendQueueRef.current.setSendFn(
      (msg) => wsClientRef.current?.sendMessage(msg) ?? Promise.resolve(false),
    );

    // 기본 핸들러 등록
    messageQueueRef.current.registerHandler("ERROR", async (data) => {
      // 에러 처리
      console.error("[WS] ERROR:", data);
    });
    messageQueueRef.current.registerHandler("CONNECTED_USERS", async (data) => {
      // 연결된 사용자 처리
      console.log("[WS] CONNECTED_USERS:", data.users);
    });

    wsClientRef.current.connect();
    return () => {
      wsClientRef.current?.cleanup();
      wsClientRef.current = null;
      sendQueueRef.current.cleanup();
      messageQueueRef.current.cleanup();
      // messageQueueRef.current = null;
    };
  }, []);

  const sendMessage = (message: string | object) => {
    const processedMessage =
      typeof message === "string" ? message : JSON.stringify(message);
    sendQueueRef.current.enqueue(processedMessage);
    console.log("[WS] Send message:", message);
  };

  const value = {
    wsClientRef,
    isConnected,
    sendMessage,
    registerMessageHandler: (type: string, handler: MessageHandler) =>
      messageQueueRef.current.registerHandler(type, handler),
    unregisterMessageHandler: (type: string) =>
      messageQueueRef.current.unregisterHandler(type),
  };

  const renderNotConnected = () => {
    if (maintenanceMessage.value) {
      return (
        <Alert className="bg-destructive/95 text-destructive-foreground backdrop-blur-sm">
          <AlertCircleIcon />
          <AlertTitle>{t("Maintenance")}</AlertTitle>
          <AlertDescription className="mt-3">
            <pre>{maintenanceMessage.value}</pre>
          </AlertDescription>
        </Alert>
      );
    } else {
      return (
        <Button
          variant="destructive"
          onClick={() => wsClientRef.current?.reconnectAgain()}
        >
          {t("IsConnecting")}
        </Button>
      );
    }
  };

  return (
    <SocketProviderContext.Provider {...props} value={value}>
      {children}
      {!isConnected && (
        <div className="fixed top-2 left-1/2 transform -translate-x-1/2 z-50">
          {renderNotConnected()}
        </div>
      )}
    </SocketProviderContext.Provider>
  );
}

export const useSocket = () => {
  const context = useContext(SocketProviderContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};
